import { Button } from '@progress/kendo-react-buttons';
import cloneDeep from 'lodash.clonedeep';
import { useContext, useCallback, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { GameDocumentContext } from '../../contexts/game-document';
import { uuid } from '../../types/common-helper';
import { GameDocument } from '../../types/game-document';
import {
  FormCustomFeedback,
  TaskContentForm
} from '../../types/game-document/entities/task-content';
import {
  AddResourceAsync,
  AllCorrectCustomFeedback,
  AllWrongCustomFeedback,
  GetResourceEntity,
  GetResourceValue,
  GetTaskContentById,
  PartialCorrectCustomFeedback,
  UpdateGameDocState,
  UpdateResourceAsync,
  UpdateTaskContentAsync
} from '../../utils/game-document';
import { CustomFeedbackMessage } from './custom-feedback-message';
import { defaultFeedbackMessage } from '../../constants/feedback-content';
import { UploadedImage } from '../../features/game-document/image-resource/resource-window';

interface CustomFeedbackProps {
  taskContentId: string;
  isShowPartial?: boolean;
  gameDocument: GameDocument;
  entity?: FormCustomFeedback;
  taskContentForm: TaskContentForm;
  onCancel?: () => void;
  onSubmit?: (
    entity: FormCustomFeedback,
    uploadedImage: {
      allCorrectUrlResId: UploadedImage | null;
      allWrongUrlResId: UploadedImage | null;
      partiallyCorrectUrlResId: UploadedImage | null;
    }
  ) => void;
}

const defaultImage = {
  defaultFeedbackAllCorrectIconResId:
    'https://stsharedappstorage.blob.core.windows.net/prod-catalyst-files/checked.png',
  defaultFeedbackPartialCorrectIconResId:
    'https://stsharedappstorage.blob.core.windows.net/prod-catalyst-files/checked.png',
  defaultFeedbackAllWrongIconResId:
    'https://stsharedappstorage.blob.core.windows.net/prod-catalyst-files/cross.png'
};

export const CustomFeedback = ({ ...props }: CustomFeedbackProps) => {
  const [entity, setEntity] = useState<FormCustomFeedback>(props.entity ?? {});
  const [state, setState] = useContext(GameDocumentContext);
  const [uploadedImage, setUploadedImage] = useState<{
    allCorrectUrlResId: UploadedImage | null;
    allWrongUrlResId: UploadedImage | null;
    partiallyCorrectUrlResId: UploadedImage | null;
  }>({
    allCorrectUrlResId: null,
    allWrongUrlResId: null,
    partiallyCorrectUrlResId: null
  });
  const [messageValue, setMessageValue] = useState<{
    allCorrectMessage: string;
    allWrongMessage: string;
    partiallyCorrectMessage: string;
  }>({
    allCorrectMessage: GetResourceValue(
      state.gameDocument!,
      entity?.allCorrectMessageResId ?? ''
    ),
    allWrongMessage: GetResourceValue(
      state.gameDocument!,
      entity?.allWrongMessageResId ?? ''
    ),
    partiallyCorrectMessage: GetResourceValue(
      state.gameDocument!,
      entity?.partiallyCorrectMessageResId ?? ''
    )
  });

  const handleResourceSubmit = async (
    allCorrectMessage?: string,
    partialCorrectMessage?: string,
    allWrongMessage?: string,
    cloneEntity?: FormCustomFeedback
  ) => {
    if (!cloneEntity?.isDefaultAllCorrect) {
      if (!cloneEntity?.allCorrectMessageResId) {
        addNewCustomFeedback(
          'All correct feedback message custom',
          allCorrectMessage ?? '',
          'allCorrect'
        );
      } else {
        updateCustomFeedback('allCorrect');
      }
    }

    if (props.isShowPartial && !cloneEntity?.isDefaultPartiallyCorrect) {
      if (!cloneEntity?.partiallyCorrectMessageResId) {
        addNewCustomFeedback(
          'Partial correct feedback message custom',
          partialCorrectMessage ?? '',
          'partialCorrect'
        );
      } else {
        updateCustomFeedback('partialCorrect');
      }
    }

    if (!cloneEntity?.isDefaultAllWrong) {
      if (!cloneEntity?.allWrongMessageResId) {
        addNewCustomFeedback(
          'All wrong feedback message custom',
          allWrongMessage ?? '',
          'allWrong'
        );
      } else {
        updateCustomFeedback('allWrong');
      }
    }
  };

  const addNewCustomFeedback = async (
    feedbackName: string,
    feedbackMessage: string,
    action: string
  ) => {
    let uuidFeedback = uuid();

    AddResourceAsync(
      state.gameDocument!,
      feedbackName,
      '',
      'text',
      feedbackMessage,
      uuidFeedback
    ).then((response) => {
      // set entity state for local storage
      let newEntity = cloneDeep(entity);
      if (action === 'allCorrect') {
        newEntity['allCorrectMessageResId'] = uuidFeedback;
      } else if (action === 'partialCorrect') {
        newEntity['partiallyCorrectMessageResId'] = uuidFeedback;
      } else if (action === 'allWrong') {
        newEntity['allWrongMessageResId'] = uuidFeedback;
      }
      setEntity(newEntity);

      // update task content for messageResId update
      let newTaskContentForm = cloneDeep(props.taskContentForm);
      if (action === 'allCorrect') {
        newTaskContentForm!.customFeedback!.allCorrectMessageResId =
          uuidFeedback;
      } else if (action === 'partialCorrect') {
        newTaskContentForm!.customFeedback!.partiallyCorrectMessageResId =
          uuidFeedback;
      } else if (action === 'allWrong') {
        newTaskContentForm!.customFeedback!.allWrongMessageResId = uuidFeedback;
      }

      let taskContent = GetTaskContentById(
        props.gameDocument,
        props.taskContentId
      );
      let taskContentFormIndex = taskContent?.forms!.findIndex(
        (item) => item.id === newTaskContentForm.id
      );
      taskContent!.forms![taskContentFormIndex!] = newTaskContentForm;

      UpdateTaskContentAsync(
        response,
        props.taskContentForm.id,
        taskContent!
      ).then((responseUpdateTask) => {
        setState((prev) => UpdateGameDocState(prev, responseUpdateTask));
      });
    });
  };

  const addDefaultCustomFeedbackToResource = async (
    feedbackName: string,
    feedbackMessage: string,
    action: string,
    uuidFeedback: string
  ) => {
    AddResourceAsync(
      state.gameDocument!,
      feedbackName,
      '',
      'text',
      feedbackMessage,
      uuidFeedback
    ).then((response) => {
      // set entity state for local storage
      let newEntity = cloneDeep(entity);
      if (action === 'allCorrect') {
        newEntity['allCorrectMessageResId'] = uuidFeedback;
      } else if (action === 'partialCorrect') {
        newEntity['partiallyCorrectMessageResId'] = uuidFeedback;
      } else if (action === 'allWrong') {
        newEntity['allWrongMessageResId'] = uuidFeedback;
      }
      setEntity(newEntity);

      // update task content for messageResId update
      let newTaskContentForm = cloneDeep(props.taskContentForm);
      if (action === 'allCorrect') {
        newTaskContentForm!.customFeedback!.allCorrectMessageResId =
          uuidFeedback;
      } else if (action === 'partialCorrect') {
        newTaskContentForm!.customFeedback!.partiallyCorrectMessageResId =
          uuidFeedback;
      } else if (action === 'allWrong') {
        newTaskContentForm!.customFeedback!.allWrongMessageResId = uuidFeedback;
      }

      let taskContent = GetTaskContentById(
        props.gameDocument,
        props.taskContentId
      );
      let taskContentFormIndex = taskContent?.forms!.findIndex(
        (item) => item.id === newTaskContentForm.id
      );
      taskContent!.forms![taskContentFormIndex!] = newTaskContentForm;

      UpdateTaskContentAsync(
        response,
        props.taskContentForm.id,
        taskContent!
      ).then((responseUpdateTask) => {
        setState((prev) => UpdateGameDocState(prev, responseUpdateTask));
      });
    });
  };

  const updateCustomFeedback = async (action: string) => {
    let messageResId = '';
    let message = '';

    if (action === 'allCorrect') {
      messageResId = entity?.allCorrectMessageResId!;
      message = messageValue.allCorrectMessage;
    } else if (action === 'partialCorrect') {
      messageResId = entity?.partiallyCorrectMessageResId!;
      message = messageValue.partiallyCorrectMessage;
    } else if (action === 'allWrong') {
      messageResId = entity?.allWrongMessageResId!;
      message = messageValue.allWrongMessage;
    }

    let customFeedbackResource = GetResourceEntity(
      state.gameDocument!,
      messageResId
    );
    customFeedbackResource.value = message;
    await UpdateResourceAsync(
      state.gameDocument!,
      messageResId,
      customFeedbackResource
    );
  };

  const imageUrl = useCallback(
    (
      res:
        | 'defaultFeedbackAllCorrectIconResId'
        | 'defaultFeedbackPartialCorrectIconResId'
        | 'defaultFeedbackAllWrongIconResId'
    ) => {
      if (
        state.gameDocument?.settings &&
        state.gameDocument?.settings.designer
      ) {
        const feedbackIcon = GetResourceValue(
          state.gameDocument,
          state.gameDocument.settings.designer[res]
        );

        return feedbackIcon !== '' ? feedbackIcon : defaultImage[res];
      } else {
        return defaultImage[res];
      }
    },
    [state.gameDocument?.settings]
  );

  const defaultMessage = useCallback(
    (
      type:
        | 'defaultAllCorrectFeedbackMessageResId'
        | 'defaultPartialCorrectFeedbackMessageResId'
        | 'defaultAllWrongFeedbackMessageResId'
    ) => {
      if (
        state.gameDocument?.settings &&
        state.gameDocument?.settings.designer
      ) {
        const message = GetResourceValue(
          state.gameDocument,
          state.gameDocument.settings.designer[type] ?? ''
        );

        const value =
          message == '' ||
          (type === 'defaultAllCorrectFeedbackMessageResId' &&
            message === 'All correct feedback message default') ||
          (type === 'defaultAllWrongFeedbackMessageResId' &&
            message === 'All wrong feedback message default') ||
          (type === 'defaultPartialCorrectFeedbackMessageResId' &&
            message === 'Well done, you are partially correct.')
            ? defaultFeedbackMessage[type]
            : message;
        return value;
      }

      return defaultFeedbackMessage[type];
    },
    [state.gameDocument?.settings]
  );

  const onImageChange = (
    uploadedImage: UploadedImage,
    type: 'allCorrectUrlResId' | 'allWrongUrlResId' | 'partiallyCorrectUrlResId'
  ) => {
    const resId = uploadedImage.resource?.id;
    if (resId) {
      setEntity((prev) => ({
        ...prev,
        [type]: resId
      }));
    } else {
      setUploadedImage((prev) => ({
        ...prev,
        [type]: uploadedImage
      }));
    }
  };

  return (
    <>
      <Row>
        <Col lg={4} md={12} sm={12} xs={12}>
          <CustomFeedbackMessage
            title={'Excellent!'}
            message={
              entity?.isDefaultAllCorrect
                ? defaultMessage('defaultAllCorrectFeedbackMessageResId')
                : messageValue.allCorrectMessage
            }
            isDefault={entity?.isDefaultAllCorrect}
            url={
              entity?.isDefaultAllCorrect
                ? imageUrl('defaultFeedbackAllCorrectIconResId')
                : GetResourceValue(
                    state?.gameDocument!,
                    entity?.allCorrectUrlResId ?? ''
                  ) || imageUrl('defaultFeedbackAllCorrectIconResId')
            }
            onMessageChange={(message) =>
              setMessageValue((prev) => ({
                ...prev,
                allCorrectMessage: message
              }))
            }
            onChangeIsDefault={(value) => {
              if (!value)
                setMessageValue((prev) => ({
                  ...prev,
                  allCorrectMessage: defaultMessage(
                    'defaultAllCorrectFeedbackMessageResId'
                  )
                }));

              setEntity((prev) => ({
                ...prev,
                isDefaultAllCorrect: value,
                allCorrectUrlResId: value ? '' : entity?.allCorrectUrlResId
              }));
            }}
            onImageChange={(uploadedImage) =>
              onImageChange(uploadedImage, 'allCorrectUrlResId')
            }
          />
        </Col>
        {props.isShowPartial && (
          <Col lg={4} md={12} sm={12} xs={12}>
            <CustomFeedbackMessage
              title={'Partially correct'}
              message={
                entity?.isDefaultPartiallyCorrect
                  ? defaultMessage('defaultPartialCorrectFeedbackMessageResId')
                  : messageValue.partiallyCorrectMessage
              }
              isDefault={entity?.isDefaultPartiallyCorrect}
              url={
                entity?.isDefaultPartiallyCorrect
                  ? imageUrl('defaultFeedbackPartialCorrectIconResId')
                  : GetResourceValue(
                      state?.gameDocument!,
                      entity?.partiallyCorrectUrlResId ?? ''
                    ) || imageUrl('defaultFeedbackPartialCorrectIconResId')
              }
              onMessageChange={(message) =>
                setMessageValue((prev) => ({
                  ...prev,
                  partiallyCorrectMessage: message
                }))
              }
              onChangeIsDefault={(value) => {
                if (!value)
                  setMessageValue((prev) => ({
                    ...prev,
                    partiallyCorrectMessage: defaultMessage(
                      'defaultPartialCorrectFeedbackMessageResId'
                    )
                  }));

                setEntity((prev) => ({
                  ...prev,
                  isDefaultPartiallyCorrect: value,
                  partiallyCorrectUrlResId: value
                    ? ''
                    : entity?.partiallyCorrectUrlResId
                }));
              }}
              onImageChange={(uploadedImage) =>
                onImageChange(uploadedImage, 'partiallyCorrectUrlResId')
              }
            />
          </Col>
        )}
        <Col lg={4} md={12} sm={12} xs={12}>
          <CustomFeedbackMessage
            title={'Unlucky.'}
            message={
              entity?.isDefaultAllWrong
                ? defaultMessage('defaultAllWrongFeedbackMessageResId')
                : messageValue.allWrongMessage
            }
            isDefault={entity?.isDefaultAllWrong}
            url={
              entity?.isDefaultAllWrong
                ? imageUrl('defaultFeedbackAllWrongIconResId')
                : GetResourceValue(
                    state?.gameDocument!,
                    entity?.allWrongUrlResId ?? ''
                  ) || imageUrl('defaultFeedbackAllWrongIconResId')
            }
            onMessageChange={(message) =>
              setMessageValue((prev) => ({
                ...prev,
                allWrongMessage: message
              }))
            }
            onChangeIsDefault={(value) => {
              if (!value)
                setMessageValue((prev) => ({
                  ...prev,
                  allWrongMessage: defaultMessage(
                    'defaultAllWrongFeedbackMessageResId'
                  )
                }));

              setEntity((prev) => ({
                ...prev,
                isDefaultAllWrong: value,
                allWrongUrlResId: value ? '' : entity?.allWrongUrlResId
              }));
            }}
            onImageChange={(uploadedImage) =>
              onImageChange(uploadedImage, 'allWrongUrlResId')
            }
          />
        </Col>
      </Row>
      <Row>
        <Col className={'d-flex justify-content-end mt-3'}>
          <Button
            themeColor={'light'}
            className={'mr-2'}
            onClick={() => {
              if (props.onCancel) {
                props.onCancel();
              }
            }}>
            Cancel
          </Button>
          <Button
            themeColor={'primary'}
            onClick={async () => {
              if (props.onSubmit) {
                const cloneEntity = cloneDeep(entity);

                if (
                  cloneEntity.isDefaultAllCorrect &&
                  cloneEntity.allCorrectMessageResId === ''
                ) {
                  const correctMessage = await AllCorrectCustomFeedback(
                    state.gameDocument!,
                    defaultMessage('defaultAllCorrectFeedbackMessageResId')
                  );
                  const correctMessageId = uuid();

                  cloneEntity.allCorrectMessageResId =
                    cloneEntity.allCorrectMessageResId === ''
                      ? correctMessageId
                      : cloneEntity.allCorrectMessageResId;

                  await addDefaultCustomFeedbackToResource(
                    'All correct feedback message custom',
                    correctMessage ?? '',
                    'allCorrect',
                    correctMessageId
                  );
                }

                if (
                  props.isShowPartial &&
                  cloneEntity.isDefaultPartiallyCorrect &&
                  cloneEntity.partiallyCorrectMessageResId === ''
                ) {
                  const partialyMessage = await PartialCorrectCustomFeedback(
                    state.gameDocument!,
                    defaultMessage('defaultPartialCorrectFeedbackMessageResId')
                  );
                  const partialyMessageId = uuid();
                  cloneEntity.partiallyCorrectMessageResId =
                    cloneEntity.partiallyCorrectMessageResId === ''
                      ? partialyMessageId
                      : cloneEntity.partiallyCorrectMessageResId;

                  await addDefaultCustomFeedbackToResource(
                    'Partial correct feedback message custom',
                    partialyMessage ?? '',
                    'partialCorrect',
                    partialyMessageId
                  );
                }

                if (
                  cloneEntity.isDefaultAllWrong &&
                  cloneEntity.allWrongMessageResId === ''
                ) {
                  const wrongMessage = await AllWrongCustomFeedback(
                    state.gameDocument!,
                    defaultMessage('defaultAllWrongFeedbackMessageResId')
                  );
                  const wrongMessageId = uuid();
                  cloneEntity.allWrongMessageResId =
                    cloneEntity.allWrongMessageResId === ''
                      ? wrongMessageId
                      : cloneEntity.allWrongMessageResId;

                  await addDefaultCustomFeedbackToResource(
                    'All wrong feedback message custom',
                    wrongMessage ?? '',
                    'allWrong',
                    wrongMessageId
                  );
                }

                handleResourceSubmit(
                  messageValue.allCorrectMessage ?? undefined,
                  messageValue.partiallyCorrectMessage ?? undefined,
                  messageValue.allWrongMessage ?? undefined,
                  cloneEntity
                ).then(() => {
                  props!.onSubmit!(cloneEntity!, uploadedImage);
                });
              }
            }}>
            Confirm
          </Button>
        </Col>
      </Row>
    </>
  );
};
